import React from "react";
import { TrueThreatButton } from "./TrueThreatButton";
import { TrueNonThreatButton } from "./TrueNonThreatButton";
import { AlertData } from "./AlertsUtils/alertsInterfaces";
import { FalseButton } from "./FalseButton";
import { Dispatcher } from "../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";
import { NavigateFunction } from "react-router-dom";
import { handleCreateAlertTypeNoUnderscore } from "./AlertsTable";
import { EscalatedTrueThreatButton } from "./EscalatedTrueThreatButton";
import { handleTrueThreatClick } from "./TrueThreatButton";
import { handleTrueNonThreatClick } from "./TrueNonThreatButton";
import { handleFalseThreatClick } from "./FalseButton";
import { handleEscalatedTrueThreatClick } from "./EscalatedTrueThreatButton";
import { VlmFilterType } from "./AlertsUtils/vlmFilters";
import { useAppStore } from "../../../appStore";
import { canSetAlertOutcome } from "./AlertsUtils/alertOutcomePermission";

export const AlertsIdentifierBox = ({
  selectedRow,
  alertData,
  setAlertData,
  setSelectedRow,
  selectdButtonsFilled,
  setSelectedButtonsFilled,
  navigate,
  setPageLoading,
  setButtonClicked,
  pageLoadedNumber,
  setPageLoadedNumber,
  setFetchingFilteredData,
  setSeeMoreURL,
  setMP4URL,
  userFilters,
  setThumbnailVideos,
  setDateOutsideRange,
  setPage,
  setVideoClipLoading,
  setAlertsToDisplay,
  setSelectedAlertCameraInfo,
  setIgnoreZonesSelectedAlert,
  setAlertsIndexOrder,
  setRefreshInterval,
  setCurrentName,
  setUserFilters,
  setRefreshingData,
  refreshInterval,
  cameraOptions,
  siteOptions,
  setRefreshingToken,
  refreshingToken,
  setAlertLabels,
  setPeriodHours,
  setQueryLimitHit,
  setForwardArrowDisabled,
  setBackArrowDisabled,
  setShowRefreshButton,
  setRowsSortable,
  setAlertsRefreshing,
  // New props for auto-advance functionality
  detectionType,
  outcomeFilterType,
  vlmFilterType,
  getCurrentSelectedRowKey,
  setVideoProcessing,
  useAsyncGeneration,
  syncSelectedRowRef,
  compact = false,
}: {
  selectedRow: AlertData;
  alertData: AlertData[];
  setAlertData: Dispatcher<AlertData[]>;
  setSelectedRow: Dispatcher<AlertData>;
  selectdButtonsFilled: any;
  setSelectedButtonsFilled: any;
  navigate: NavigateFunction;
  setPageLoading: Dispatcher<boolean>;
  setButtonClicked: Dispatcher<boolean>;
  pageLoadedNumber: any;
  setPageLoadedNumber: Dispatcher<any>;
  setFetchingFilteredData: Dispatcher<boolean>;
  setSeeMoreURL: Dispatcher<string>;
  setMP4URL: Dispatcher<string>;
  userFilters: any;
  setThumbnailVideos: any;
  setDateOutsideRange: Dispatcher<boolean>;
  setPage: Dispatcher<number>;
  setVideoClipLoading: Dispatcher<boolean>;
  setAlertsToDisplay: Dispatcher<boolean>;
  setSelectedAlertCameraInfo: any;
  setIgnoreZonesSelectedAlert: any;
  setAlertsIndexOrder: any;
  setRefreshInterval: any;
  setCurrentName: any;
  setUserFilters: any;
  setRefreshingData: any;
  refreshInterval: number;
  cameraOptions: any;
  siteOptions: any;
  setRefreshingToken: Dispatcher<boolean>;
  refreshingToken: boolean;
  setAlertLabels: Dispatcher<string[]>;
  setPeriodHours: Dispatcher<number>;
  setQueryLimitHit: Dispatcher<boolean>;
  setForwardArrowDisabled: Dispatcher<boolean>;
  setBackArrowDisabled: Dispatcher<boolean>;
  setShowRefreshButton: Dispatcher<boolean>;
  setRowsSortable: Dispatcher<boolean>;
  setAlertsRefreshing: Dispatcher<boolean>;
  // New props for auto-advance functionality
  detectionType: string;
  outcomeFilterType: string;
  vlmFilterType: VlmFilterType;
  getCurrentSelectedRowKey: () => string | null;
  setVideoProcessing: Dispatcher<boolean>;
  useAsyncGeneration: boolean;
  syncSelectedRowRef: (row: any) => void;
  compact?: boolean;
}) => {
  // ENG-1813: the outcome buttons, their prompt and the t/n/f/e shortcuts are
  // only offered to users whose /user/info/ says they may set outcomes. The
  // monitoring API checks the same permission, so this just keeps the UI in
  // step with what it will accept. Reading the store (not a prop) means a
  // stale grant revoked mid-session un-renders the box reactively.
  const { userPermissions } = useAppStore();
  const canSetOutcome = canSetAlertOutcome(userPermissions);

  // Keyboard shortcuts handler
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!canSetOutcome) {
        return;
      }
      // Only handle shortcuts when not in a dialog or input field
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLSelectElement
      ) {
        return;
      }

      switch (event.key.toLowerCase()) {
        case "t":
          event.preventDefault();
          setButtonClicked(true);
          handleTrueThreatClick(
            alertData,
            setAlertData,
            selectedRow,
            setSelectedRow,
            navigate,
            setSelectedButtonsFilled,
            setPageLoading,
            setButtonClicked,
            setMP4URL,
            setVideoClipLoading,
            setSeeMoreURL,
            setForwardArrowDisabled,
            setBackArrowDisabled,
            setShowRefreshButton,
            setSelectedAlertCameraInfo,
            setIgnoreZonesSelectedAlert,
            userFilters,
            detectionType,
            outcomeFilterType,
            vlmFilterType,
            getCurrentSelectedRowKey,
            setVideoProcessing,
            useAsyncGeneration,
            syncSelectedRowRef
          );
          break;
        case "n":
          event.preventDefault();
          setButtonClicked(true);
          handleTrueNonThreatClick(
            alertData,
            setAlertData,
            selectedRow,
            setSelectedRow,
            navigate,
            setSelectedButtonsFilled,
            setPageLoading,
            setButtonClicked,
            setMP4URL,
            setVideoClipLoading,
            setSeeMoreURL,
            setForwardArrowDisabled,
            setBackArrowDisabled,
            setShowRefreshButton,
            setSelectedAlertCameraInfo,
            setIgnoreZonesSelectedAlert,
            userFilters,
            detectionType,
            outcomeFilterType,
            vlmFilterType,
            getCurrentSelectedRowKey,
            setVideoProcessing,
            useAsyncGeneration,
            syncSelectedRowRef
          );
          break;
        case "f":
          event.preventDefault();
          setButtonClicked(true);
          handleFalseThreatClick(
            alertData,
            setAlertData,
            selectedRow,
            setSelectedRow,
            navigate,
            setSelectedButtonsFilled,
            setPageLoading,
            setButtonClicked,
            setMP4URL,
            setVideoClipLoading,
            setSeeMoreURL,
            setForwardArrowDisabled,
            setBackArrowDisabled,
            setShowRefreshButton,
            setSelectedAlertCameraInfo,
            setIgnoreZonesSelectedAlert,
            userFilters,
            detectionType,
            outcomeFilterType,
            vlmFilterType,
            getCurrentSelectedRowKey,
            setVideoProcessing,
            useAsyncGeneration,
            syncSelectedRowRef
          );
          break;
        case "e":
          event.preventDefault();
          setButtonClicked(true);
          handleEscalatedTrueThreatClick(
            alertData,
            setAlertData,
            selectedRow,
            setSelectedRow,
            navigate,
            setSelectedButtonsFilled,
            setPageLoading,
            setButtonClicked,
            setMP4URL,
            setVideoClipLoading,
            setSeeMoreURL,
            setForwardArrowDisabled,
            setBackArrowDisabled,
            setShowRefreshButton,
            setSelectedAlertCameraInfo,
            setIgnoreZonesSelectedAlert,
            userFilters,
            detectionType,
            outcomeFilterType,
            vlmFilterType,
            getCurrentSelectedRowKey,
            setVideoProcessing,
            useAsyncGeneration,
            syncSelectedRowRef
          );
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    alertData,
    setAlertData,
    selectedRow,
    setSelectedRow,
    navigate,
    setSelectedButtonsFilled,
    setPageLoading,
    setButtonClicked,
    pageLoadedNumber,
    setPageLoadedNumber,
    setFetchingFilteredData,
    setSeeMoreURL,
    setMP4URL,
    userFilters,
    setThumbnailVideos,
    setDateOutsideRange,
    setPage,
    setVideoClipLoading,
    setAlertsToDisplay,
    setSelectedAlertCameraInfo,
    setIgnoreZonesSelectedAlert,
    setAlertsIndexOrder,
    setRefreshInterval,
    setCurrentName,
    setUserFilters,
    setRefreshingData,
    refreshInterval,
    cameraOptions,
    siteOptions,
    setRefreshingToken,
    refreshingToken,
    setAlertLabels,
    setPeriodHours,
    setQueryLimitHit,
    setForwardArrowDisabled,
    setBackArrowDisabled,
    setShowRefreshButton,
    setRowsSortable,
    setAlertsRefreshing,
    detectionType,
    outcomeFilterType,
    vlmFilterType,
    getCurrentSelectedRowKey,
    setVideoProcessing,
    useAsyncGeneration,
    syncSelectedRowRef,
    canSetOutcome,
  ]);

  if (!canSetOutcome) {
    return null;
  }

  return selectedRow.detection_type === "escalated_true_threat" ||
    selectedRow.detection_type === "true_threat" ? (
    <div className="flex w-full grow flex-col justify-center rounded border border-actuate-grey bg-[#F0F1F3] pt-2 pb-[10px]">
      <div className="px-2 text-center font-mulish text-[14px] text-actuate-blue">
        {selectedRow.alert_labels
          ? `Was this alert a true ${handleCreateAlertTypeNoUnderscore(
              selectedRow.alert_labels
            )} detection?`
          : ""}
      </div>
      <div className="grid w-full min-w-0 grid-cols-2 gap-2 px-2 pt-2">
        <div className="flex w-full min-w-0 justify-center">
          <EscalatedTrueThreatButton
            detection_type={selectedRow.detection_type}
            alertData={alertData}
            setAlertData={setAlertData}
            selectedRow={selectedRow}
            setSelectedRow={setSelectedRow}
            selectdButtonsFilled={selectdButtonsFilled}
            setSelectedButtonsFilled={setSelectedButtonsFilled}
            navigate={navigate}
            setPageLoading={setPageLoading}
            setButtonClicked={setButtonClicked}
            setMP4URL={setMP4URL}
            setVideoClipLoading={setVideoClipLoading}
            setSeeMoreURL={setSeeMoreURL}
            setForwardArrowDisabled={setForwardArrowDisabled}
            setBackArrowDisabled={setBackArrowDisabled}
            setShowRefreshButton={setShowRefreshButton}
            setSelectedAlertCameraInfo={setSelectedAlertCameraInfo}
            setIgnoreZonesSelectedAlert={setIgnoreZonesSelectedAlert}
            userFilters={userFilters}
            detectionType={detectionType}
            outcomeFilterType={outcomeFilterType}
            vlmFilterType={vlmFilterType}
            getCurrentSelectedRowKey={getCurrentSelectedRowKey}
            setVideoProcessing={setVideoProcessing}
            useAsyncGeneration={useAsyncGeneration}
            syncSelectedRowRef={syncSelectedRowRef}
            compact={compact}
          />
        </div>
        <div className="flex w-full min-w-0 justify-center">
          <TrueThreatButton
            selectdButtonsFilled={selectdButtonsFilled}
            setSelectedButtonsFilled={setSelectedButtonsFilled}
            alertData={alertData}
            setAlertData={setAlertData}
            selectedRow={selectedRow}
            setSelectedRow={setSelectedRow}
            navigate={navigate}
            setPageLoading={setPageLoading}
            setButtonClicked={setButtonClicked}
            setMP4URL={setMP4URL}
            setVideoClipLoading={setVideoClipLoading}
            setSeeMoreURL={setSeeMoreURL}
            setForwardArrowDisabled={setForwardArrowDisabled}
            setBackArrowDisabled={setBackArrowDisabled}
            setShowRefreshButton={setShowRefreshButton}
            setSelectedAlertCameraInfo={setSelectedAlertCameraInfo}
            setIgnoreZonesSelectedAlert={setIgnoreZonesSelectedAlert}
            userFilters={userFilters}
            detectionType={detectionType}
            outcomeFilterType={outcomeFilterType}
            vlmFilterType={vlmFilterType}
            getCurrentSelectedRowKey={getCurrentSelectedRowKey}
            setVideoProcessing={setVideoProcessing}
            useAsyncGeneration={useAsyncGeneration}
            syncSelectedRowRef={syncSelectedRowRef}
            compact={compact}
          />
        </div>
      </div>
      <div className="grid w-full min-w-0 grid-cols-2 gap-2 px-2 pt-[15px]">
        <div className="flex w-full min-w-0 justify-center">
          <TrueNonThreatButton
            detection_type={selectedRow.detection_type}
            alertData={alertData}
            setAlertData={setAlertData}
            selectedRow={selectedRow}
            setSelectedRow={setSelectedRow}
            selectdButtonsFilled={selectdButtonsFilled}
            setSelectedButtonsFilled={setSelectedButtonsFilled}
            navigate={navigate}
            setPageLoading={setPageLoading}
            setButtonClicked={setButtonClicked}
            setMP4URL={setMP4URL}
            setVideoClipLoading={setVideoClipLoading}
            setSeeMoreURL={setSeeMoreURL}
            setForwardArrowDisabled={setForwardArrowDisabled}
            setBackArrowDisabled={setBackArrowDisabled}
            setShowRefreshButton={setShowRefreshButton}
            setSelectedAlertCameraInfo={setSelectedAlertCameraInfo}
            setIgnoreZonesSelectedAlert={setIgnoreZonesSelectedAlert}
            userFilters={userFilters}
            detectionType={detectionType}
            outcomeFilterType={outcomeFilterType}
            vlmFilterType={vlmFilterType}
            getCurrentSelectedRowKey={getCurrentSelectedRowKey}
            setVideoProcessing={setVideoProcessing}
            useAsyncGeneration={useAsyncGeneration}
            syncSelectedRowRef={syncSelectedRowRef}
            compact={compact}
          />
        </div>
        <div className="flex w-full min-w-0 justify-center">
          <FalseButton
            detection_type={selectedRow.detection_type}
            alertData={alertData}
            setAlertData={setAlertData}
            selectedRow={selectedRow}
            setSelectedRow={setSelectedRow}
            selectdButtonsFilled={selectdButtonsFilled}
            setSelectedButtonsFilled={setSelectedButtonsFilled}
            navigate={navigate}
            setPageLoading={setPageLoading}
            setButtonClicked={setButtonClicked}
            setMP4URL={setMP4URL}
            setVideoClipLoading={setVideoClipLoading}
            setSeeMoreURL={setSeeMoreURL}
            setForwardArrowDisabled={setForwardArrowDisabled}
            setBackArrowDisabled={setBackArrowDisabled}
            setShowRefreshButton={setShowRefreshButton}
            setSelectedAlertCameraInfo={setSelectedAlertCameraInfo}
            setIgnoreZonesSelectedAlert={setIgnoreZonesSelectedAlert}
            userFilters={userFilters}
            detectionType={detectionType}
            outcomeFilterType={outcomeFilterType}
            vlmFilterType={vlmFilterType}
            getCurrentSelectedRowKey={getCurrentSelectedRowKey}
            setVideoProcessing={setVideoProcessing}
            useAsyncGeneration={useAsyncGeneration}
            syncSelectedRowRef={syncSelectedRowRef}
            compact={compact}
          />
        </div>
      </div>
    </div>
  ) : (
    <div className="flex w-full grow flex-col justify-center rounded border border-actuate-grey bg-[#F0F1F3] pt-2 pb-[10px]">
      <div className="px-2 text-center font-mulish text-[14px] text-actuate-blue">
        {selectedRow.alert_labels
          ? `Was this alert a true ${handleCreateAlertTypeNoUnderscore(
              selectedRow.alert_labels
            )} detection?`
          : ""}
      </div>
      <div className="grid w-full min-w-0 grid-cols-3 gap-2 px-2 pt-2">
        <div className="flex w-full min-w-0 justify-center">
          <TrueThreatButton
            selectdButtonsFilled={selectdButtonsFilled}
            setSelectedButtonsFilled={setSelectedButtonsFilled}
            alertData={alertData}
            setAlertData={setAlertData}
            selectedRow={selectedRow}
            setSelectedRow={setSelectedRow}
            navigate={navigate}
            setPageLoading={setPageLoading}
            setButtonClicked={setButtonClicked}
            setMP4URL={setMP4URL}
            setVideoClipLoading={setVideoClipLoading}
            setSeeMoreURL={setSeeMoreURL}
            setForwardArrowDisabled={setForwardArrowDisabled}
            setBackArrowDisabled={setBackArrowDisabled}
            setShowRefreshButton={setShowRefreshButton}
            setSelectedAlertCameraInfo={setSelectedAlertCameraInfo}
            setIgnoreZonesSelectedAlert={setIgnoreZonesSelectedAlert}
            userFilters={userFilters}
            detectionType={detectionType}
            outcomeFilterType={outcomeFilterType}
            vlmFilterType={vlmFilterType}
            getCurrentSelectedRowKey={getCurrentSelectedRowKey}
            setVideoProcessing={setVideoProcessing}
            useAsyncGeneration={useAsyncGeneration}
            syncSelectedRowRef={syncSelectedRowRef}
            compact={compact}
          />
        </div>
        <div className="flex w-full min-w-0 justify-center">
          <TrueNonThreatButton
            detection_type={selectedRow.detection_type}
            alertData={alertData}
            setAlertData={setAlertData}
            selectedRow={selectedRow}
            setSelectedRow={setSelectedRow}
            selectdButtonsFilled={selectdButtonsFilled}
            setSelectedButtonsFilled={setSelectedButtonsFilled}
            navigate={navigate}
            setPageLoading={setPageLoading}
            setButtonClicked={setButtonClicked}
            setMP4URL={setMP4URL}
            setVideoClipLoading={setVideoClipLoading}
            setSeeMoreURL={setSeeMoreURL}
            setForwardArrowDisabled={setForwardArrowDisabled}
            setBackArrowDisabled={setBackArrowDisabled}
            setShowRefreshButton={setShowRefreshButton}
            setSelectedAlertCameraInfo={setSelectedAlertCameraInfo}
            setIgnoreZonesSelectedAlert={setIgnoreZonesSelectedAlert}
            userFilters={userFilters}
            detectionType={detectionType}
            outcomeFilterType={outcomeFilterType}
            vlmFilterType={vlmFilterType}
            getCurrentSelectedRowKey={getCurrentSelectedRowKey}
            setVideoProcessing={setVideoProcessing}
            useAsyncGeneration={useAsyncGeneration}
            syncSelectedRowRef={syncSelectedRowRef}
            compact={compact}
          />
        </div>
        <div className="flex w-full min-w-0 justify-center">
          <FalseButton
            detection_type={selectedRow.detection_type}
            alertData={alertData}
            setAlertData={setAlertData}
            selectedRow={selectedRow}
            setSelectedRow={setSelectedRow}
            selectdButtonsFilled={selectdButtonsFilled}
            setSelectedButtonsFilled={setSelectedButtonsFilled}
            navigate={navigate}
            setPageLoading={setPageLoading}
            setButtonClicked={setButtonClicked}
            setMP4URL={setMP4URL}
            setVideoClipLoading={setVideoClipLoading}
            setSeeMoreURL={setSeeMoreURL}
            setForwardArrowDisabled={setForwardArrowDisabled}
            setBackArrowDisabled={setBackArrowDisabled}
            setShowRefreshButton={setShowRefreshButton}
            setSelectedAlertCameraInfo={setSelectedAlertCameraInfo}
            setIgnoreZonesSelectedAlert={setIgnoreZonesSelectedAlert}
            userFilters={userFilters}
            detectionType={detectionType}
            outcomeFilterType={outcomeFilterType}
            vlmFilterType={vlmFilterType}
            getCurrentSelectedRowKey={getCurrentSelectedRowKey}
            setVideoProcessing={setVideoProcessing}
            useAsyncGeneration={useAsyncGeneration}
            syncSelectedRowRef={syncSelectedRowRef}
            compact={compact}
          />
        </div>
      </div>
    </div>
  );
};
